import { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { useLocation } from "wouter";
import * as crmApi from "@/lib/crm-api";
import { setAuthFailureHandler } from "@/lib/queryClient";

interface User {
  id: string;
  email: string;
  nome: string;
  cognome: string;
  ruolo: "admin" | "manager" | "operator" | "viewer";
  permissions?: string;
  active: boolean;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  isLoading: boolean;
  mustChangePassword: boolean;
  login: (email: string, password: string, rememberMe?: boolean) => Promise<void>;
  logout: () => void;
  refreshToken: () => Promise<void>;
  clearMustChangePassword: () => void;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [mustChangePassword, setMustChangePassword] = useState(false);
  const [, setLocation] = useLocation();

  // Initialize auth state from localStorage
  useEffect(() => {
    const storedToken = localStorage.getItem("crm_token");
    const storedRefreshToken = localStorage.getItem("crm_refresh_token");
    
    if (storedToken) {
      setToken(storedToken);
      crmApi.setAuthToken(storedToken);
      
      // Verify token and get user info
      crmApi.getCurrentUser()
        .then(userData => {
          setUser(userData);
          // Persist mustChangePassword from server
          if (userData.mustChangePassword) {
            setMustChangePassword(true);
          }
        })
        .catch(async (error) => {
          console.error("Token validation failed:", error);
          // Try to refresh token
          if (storedRefreshToken) {
            try {
              await refreshTokenHandler(storedRefreshToken);
            } catch {
              // Refresh failed, clear auth state
              logout();
            }
          } else {
            logout();
          }
        })
        .finally(() => {
          setIsLoading(false);
        });
    } else {
      setIsLoading(false);
    }
  }, []);

  const refreshTokenHandler = async (refreshToken: string) => {
    const response = await crmApi.refreshAuthToken(refreshToken);
    localStorage.setItem("crm_token", response.token);
    localStorage.setItem("crm_refresh_token", response.refreshToken);
    setToken(response.token);
    crmApi.setAuthToken(response.token);
    
    // Get updated user info
    const userData = await crmApi.getCurrentUser();
    setUser(userData);
    // Persist mustChangePassword from server
    if (userData.mustChangePassword) {
      setMustChangePassword(true);
    }
  };

  const login = async (email: string, password: string, rememberMe: boolean = false) => {
    try {
      const response = await crmApi.login(email, password, rememberMe);
      
      // Store tokens
      localStorage.setItem("crm_token", response.token);
      localStorage.setItem("crm_refresh_token", response.refreshToken);
      
      // Update state
      setToken(response.token);
      setUser(response.user);
      crmApi.setAuthToken(response.token);
      
      // Check if user must change password
      if (response.mustChangePassword) {
        setMustChangePassword(true);
        window.location.href = "/crm-secure/change-password-required";
      } else {
        // Redirect to dashboard using window.location for reliable navigation
        window.location.href = "/crm-secure/dashboard";
      }
    } catch (error) {
      throw error;
    }
  };

  const clearMustChangePassword = () => {
    setMustChangePassword(false);
  };

  const logout = () => {
    // Clear tokens from localStorage
    localStorage.removeItem("crm_token");
    localStorage.removeItem("crm_refresh_token");
    
    // Clear state
    setUser(null);
    setToken(null);
    crmApi.clearAuthToken();
    
    // Redirect to login
    setLocation("/crm-secure/login");
  };

  const refreshToken = async () => {
    const storedRefreshToken = localStorage.getItem("crm_refresh_token");
    if (!storedRefreshToken) {
      throw new Error("No refresh token available");
    }
    
    await refreshTokenHandler(storedRefreshToken);
  };

  // Logout "soft" quando il transport unificato (queryClient) non riesce a
  // rinnovare il token CRM (refresh fallito): pulisce lo stato e reindirizza
  // via wouter, senza `window.location.href` che azzererebbe tutta la UI.
  useEffect(() => {
    setAuthFailureHandler("crm", () => logout());
    return () => setAuthFailureHandler("crm", null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <AuthContext.Provider value={{
      user,
      token,
      isLoading,
      mustChangePassword,
      login,
      logout,
      refreshToken,
      clearMustChangePassword
    }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route Component
interface ProtectedRouteProps {
  children: ReactNode;
  requiredRole?: "admin" | "manager" | "operator" | "viewer";
}

const ROLE_HIERARCHY: Record<NonNullable<ProtectedRouteProps["requiredRole"]>, number> = {
  admin: 4,
  manager: 3,
  operator: 2,
  viewer: 1,
};

export const ProtectedRoute = ({ children, requiredRole }: ProtectedRouteProps) => {
  const { user, isLoading, mustChangePassword } = useAuth();
  const [location, setLocation] = useLocation();

  const roleInsufficient =
    !!requiredRole && !!user && ROLE_HIERARCHY[user.ruolo] < ROLE_HIERARCHY[requiredRole];

  useEffect(() => {
    if (!isLoading && !user) {
      setLocation("/crm-secure/login");
    } else if (user && mustChangePassword && location !== "/crm-secure/change-password-required") {
      // Force redirect to password change page if required
      setLocation("/crm-secure/change-password-required");
    } else if (roleInsufficient) {
      setLocation("/crm-secure/unauthorized");
    }
  }, [user, isLoading, roleInsufficient, mustChangePassword, location, setLocation]);

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center bg-slate-950">
        <div className="text-slate-400">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return null;
  }
  
  // Block access to all routes except password change if mustChangePassword is true
  if (mustChangePassword && location !== "/crm-secure/change-password-required") {
    return null;
  }

  // Ruolo insufficiente: NON renderizzare i children (niente "flash" di
  // contenuto non autorizzato) mentre l'effect reindirizza a /unauthorized.
  if (roleInsufficient) {
    return null;
  }

  return <>{children}</>;
};